// src/pages/StudentAttemptAnswers.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

const card = { border:'1px solid #e5e7eb', borderRadius:12, background:'#fff', padding:14 };
const label = { color:'#6b7280', fontSize:13 };

function pct(p, m){
    const P=Number(p), M=Number(m);
    if (!Number.isFinite(P) || !Number.isFinite(M) || M<=0) return null;
    return Math.round((P/M)*100);
}

function FileBlock({ f }){
    const mime = String(f.mimetype||'').toLowerCase();
    const isImg = mime.startsWith('image/');
    const isPdf = mime==='application/pdf' || mime.startsWith('application/pdf');
    return (
        <div style={{ border:'1px solid #eee', borderRadius:8, padding:10 }} draggable={false}>
            {isImg && f.url ? (
                <img src={f.url} alt={f.filename||'plik'} style={{ width:'100%', maxHeight:260, objectFit:'contain', borderRadius:6 }} draggable={false}/>
            ) : (
                <div style={{ fontWeight:600, userSelect:'none' }}>{f.filename || 'Załącznik'}</div>
            )}
            {f.url && (
                <div style={{ marginTop:8 }}>
                    {/* linki pozostają klikalne, ale bez zaznaczania tekstu */}
                    <a href={f.url} target="_blank" rel="noreferrer" style={{ userSelect:'none' }}>
                        {isPdf ? 'Otwórz PDF' : 'Pobierz/Otwórz'}
                    </a>
                </div>
            )}
        </div>
    );
}

function QuestionMedia({ files }){
    if (!files?.length) return null;
    return (
        <div style={{ display:'grid', gap:10 }}>
            <div style={label} aria-hidden>Załączniki do pytania</div>
            <div style={{ display:'grid', gap:12 }}>
                {files.map(f => <FileBlock key={f.file_id} f={{ ...f, filename:'Załącznik' }} />)}
            </div>
        </div>
    );
}

function AnswerView({ item }){
    const t=item.type;
    if (['single','multi','dropdown','truefalse'].includes(t)) {
        const sel = new Set(item.selected_option_ids||[]);
        return (
            <div style={{ display:'grid', gap:6 }}>
                <div style={label} aria-hidden>Twoja odpowiedź</div>
                <ul style={{ margin:0, paddingLeft:16 }}>
                    {(item.options||[]).map(o=>{
                        const chosen = sel.has(o.option_id);
                        return (
                            <li key={o.option_id} style={{ color: chosen ? '#111827' : '#6b7280', userSelect:'none' }}>
                                {chosen ? '✓ ' : ''}{o.content}
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }
    if (['text','textarea','essay','math','numeric'].includes(t)) {
        return (
            <div style={{ display:'grid', gap:6 }}>
                <div style={label} aria-hidden>Twoja odpowiedź</div>
                {item.typed?.text ? (
                    <div style={{ whiteSpace:'pre-wrap', userSelect:'none' }}>{item.typed.text}</div>
                ) : (item.typed?.numeric!=null) ? (
                    <code style={{ userSelect:'none' }}>{String(item.typed.numeric)}</code>
                ) : (
                    <div style={{ opacity:.7, userSelect:'none' }}>— brak —</div>
                )}
            </div>
        );
    }
    if (['file','record','photo','whiteboard','oral'].includes(t)) {
        const files=item.answer_files||[];
        if (!files.length) return <div style={{ opacity:.7, userSelect:'none' }}>Brak załączników</div>;
        return (
            <div style={{ display:'grid', gap:10 }}>
                <div style={label} aria-hidden>Twoje załączniki</div>
                <div style={{ display:'grid', gap:12 }}>
                    {files.map(f => <FileBlock key={f.file_id} f={f} />)}
                </div>
            </div>
        );
    }
    return <div style={{ opacity:.7, userSelect:'none' }}>—</div>;
}

export default function StudentAttemptAnswers(){
    const { id } = useParams(); // attempt_id
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [data, setData] = useState(null); // { attempt, items, ... }

    // GLOBALNE BLOKADY KOPIOWANIA na tej stronie
    useEffect(()=>{
        const block = (e)=> { e.preventDefault(); e.stopPropagation(); return false; };
        document.addEventListener('copy', block, { capture:true });
        document.addEventListener('cut', block, { capture:true });
        document.addEventListener('contextmenu', block, { capture:true });
        document.addEventListener('dragstart', block, { capture:true });
        return ()=>{
            document.removeEventListener('copy', block, { capture:true });
            document.removeEventListener('cut', block, { capture:true });
            document.removeEventListener('contextmenu', block, { capture:true });
            document.removeEventListener('dragstart', block, { capture:true });
        };
    }, []);

    useEffect(()=>{
        let cancelled=false;
        (async()=>{
            setLoading(true); setErr('');
            try{
                const r = await fetch(`/api/student/attempt/${id}/answers`, {
                    credentials:'include',
                    headers:{ ...authHeader() }
                });
                if (!r.ok) {
                    const j = await r.json().catch(()=>({}));
                    throw new Error(j?.blad || `Błąd (${r.status})`);
                }
                const j = await r.json();
                if (!cancelled) setData(j);
            }catch(e){ if (!cancelled) setErr(e.message || 'Błąd pobierania'); }
            finally { if (!cancelled) setLoading(false); }
        })();
        return ()=>{ cancelled=true; };
    }, [id]);

    const att = data?.attempt;
    const items = data?.items || [];
    const showWithPoints = data?.show_with_points;
    const notReady = data?.not_ready;

    const percent = useMemo(()=> pct(att?.attempt_points, att?.attempt_max_points), [att?.attempt_points, att?.attempt_max_points]);

    return (
        <div
            style={{
                maxWidth: 1100,  padding:'0 16px', marginTop:80,
                WebkitUserSelect:'none', MozUserSelect:'none', userSelect:'none' // blokada zaznaczania
            }}
            onCopy={(e)=>e.preventDefault()}
            onCut={(e)=>e.preventDefault()}
            onContextMenu={(e)=>e.preventDefault()}
            onDragStart={(e)=>e.preventDefault()}
        >
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                <button  className={'but-smal'} onClick={()=>nav(-1)} style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:'8px 12px' }}>
                    ← Powrót
                </button>
            </div>

            <h1 style={{ userSelect:'none' }}>Twoje odpowiedzi</h1>

            {loading ? (
                <div>Ładowanie…</div>
            ) : err ? (
                <div style={{ color:'crimson' }}>{err}</div>
            ) : !att ? (
                <div>Brak danych.</div>
            ) : notReady ? (
                <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:14, background:'#fff', userSelect:'none' }}>
                    Ten egzamin jeszcze trwa lub nie został jeszcze zamknięty — odpowiedzi będą dostępne po zakończeniu.
                </div>
            ) : (
                <div style={{ display:'grid', gap:16 }}>
                    {att.status === 'auto_graded' && (
                        <div style={{ border:'1px solid #bfdbfe', background:'#eff6ff', color:'#1e3a8a', borderRadius:8, padding:10, userSelect:'none' }}>
                            Wstępna ocena została nadana automatycznie. Prowadzący może jeszcze ją zatwierdzić po weryfikacji przebiegu egzaminu.
                        </div>
                    )}
                    {att.status === 'canceled' && (
                        <div style={{ border:'1px solid #fecaca', background:'#fee2e2', color:'#7f1d1d', borderRadius:8, padding:10, userSelect:'none' }}>
                            To podejście zostało unieważnione przez prowadzącego z powodu naruszeń zasad egzaminu.
                        </div>
                    )}

                    {showWithPoints && (
                        <div style={card} draggable={false}>
                            <div style={{ fontWeight:700, fontSize:16, userSelect:'none' }}>
                                Punkty: {att.attempt_points?att.attempt_points:'—'} / {att.attempt_max_points?att.attempt_max_points:'—'}
                                {percent!=null ? <span style={{ marginLeft:8, color:'#6b7280' }}>({percent}%)</span> : null}
                            </div>
                        </div>
                    )}

                    <div style={{ display:'grid', gap:12 }}>
                        {items.map((it, idx)=>(
                            <div key={it.question_id} style={card} draggable={false}>
                                <div style={{ fontSize:13, color:'#6b7280', marginBottom:6, userSelect:'none' }}>Pytanie {idx+1}</div>

                                <div style={{ display:'grid', gap:10 }}>
                                    <div>
                                        <div style={label} aria-hidden>Treść pytania</div>
                                        <div style={{ userSelect:'none' }}>{it.content}</div>
                                    </div>

                                    <QuestionMedia files={it.question_files} />
                                    <AnswerView item={it} />

                                    {showWithPoints && (
                                        <div style={{ display:'grid', gap:6, marginTop:6 }}>
                                            <div style={{ userSelect:'none' }}>
                                                <b>Przyznane punkty:</b> {it.answer_points!=null ? it.answer_points : '—'} / {it.max_points}
                                            </div>
                                            {it.answer_note ? (
                                                <div>
                                                    <div style={label} aria-hidden>Notatka prowadzącego</div>
                                                    <div style={{ whiteSpace:'pre-wrap', userSelect:'none' }}>{it.answer_note}</div>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display:'flex', gap:8, paddingBottom:30}}>
                        <button  className={'but-smal'} onClick={()=>nav(-1)} style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:'8px 12px' }}>
                            ← Wróć
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
